import SoftBox from "components/SoftBox";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppServices } from "hook/services";
import { useAgencyInfo } from "context/agency";
import Select from "react-select";
import { Grid } from "@mui/material";
import SoftTypography from "components/SoftTypography";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import toast from "react-hot-toast";
import { uploadImageToGhl } from "hook/services";
import { FaImages } from "react-icons/fa";
import GoogleReviews from "./Component/GoogleReviews";
import { useGooglePlace } from "context/GooglePlaceContext";
import ReactCropModal from "layouts/super-admin/settings/Configurator/components/ReactCropModal/ReactCropModal";
import GhlMediaGallery from "components/GhlMediaGallery";
import "./accountDetailTabs.css";

const FIELD_LABELS = {
  first_name: "Business name",
  city: "City",
  website: "Website",
  country: "Country",
  state: "State / region",
  zip_code: "Zip / postal code",
  address: "Street address",
  time_zone: "Time zone",
  phone: "Phone",
  slug: "Custom slug",
  description: "Business description",
  business_tags: "Listing labels",
  cover_image_aspect_ratio: "Cover aspect ratio",
  profile_image: "Profile image",
  cover_image: "Cover image",
  google_place_id: "Google Business Profile link",
};

function ContactTab({ formData, handleRefresh }) {
  const AppService = useAppServices();
  const [agency] = useAgencyInfo();
  const [businessTypes, setBusinessTypes] = useState([]);
  const [serviceTypes, setserviceTypes] = useState([]);
  const [businessTags, setBusinessTags] = useState([]);
  const [coverImage, setCoverImage] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const { placeId } = useGooglePlace();
  const [currentFormData, setcurrentFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    slug: "",
  });

  // ReactCropModal states
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [currentImageType, setCurrentImageType] = useState(""); // "profile" or "cover"
  const [coverImageAspectRatio, setCoverImageAspectRatio] = useState(currentFormData?.cover_image_aspect_ratio || "5:2");
  const formDataRef = useRef(formData);
  const descriptionSaveTimer = useRef(null);
  const pendingTagOrderRef = useRef(null);
  const submittedTagsRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve());

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (formData) {
      let tags = formData.business_tags;
      if (pendingTagOrderRef.current) {
        tags = pendingTagOrderRef.current;
        pendingTagOrderRef.current = null;
      } else if (Array.isArray(tags)) {
        const orderOf = (tag, index) =>
          tag?.order != null && Number.isFinite(Number(tag.order)) ? Number(tag.order) : index;
        tags = tags
          .map((tag, index) => ({ tag, index }))
          .sort((a, b) => orderOf(a.tag, a.index) - orderOf(b.tag, b.index))
          .map(({ tag }) => tag);
      }
      submittedTagsRef.current = Array.isArray(tags) ? tags : null;
      setcurrentFormData({ ...formData, business_tags: tags });
      setCoverImageAspectRatio(formData?.cover_image_aspect_ratio || "5:2");
    }
  }, [formData]);

  const GetBusinessTypes = async () => {
    const { response } = await AppService.business_types.get_all_types({
      query: `agency_id=${agency._id}`,
    });
    if (response) {
      setBusinessTypes(
        response.data.map((item) => ({
          label: item.name,
          value: item._id,
        }))
      );
      setserviceTypes(
        response.service_types.map((item) => ({
          label: item.name,
          value: item._id,
        }))
      );
      // Do not sort by global tag.order here — listing label display order is
      // the order tags are selected/saved on the account, not agency reorder.
      setBusinessTags(
        (response.tags || []).map((item) => ({
          label: item.name,
          value: item._id,
        }))
      );
    }
  };

  // Sync currentFormData with formData when formData changes
  useEffect(() => {
    GetBusinessTypes();
  }, []);

  useEffect(() => {
    return () => {
      if (descriptionSaveTimer.current) {
        clearTimeout(descriptionSaveTimer.current);
      }
    };
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setcurrentFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const persistFields = useCallback(
    (fields, successMessage) => {
      const run = async () => {
        const accountId = currentFormData?.id ?? formData?.id;
        if (!accountId) {
          toast.error("Business account not found");
          return false;
        }

        try {
          const { response, error } = await AppService.accounts.update({
            payload: { _id: accountId, ...fields },
          });
          if (error || response?.success === false) {
            toast.error(error?.message || response?.message || "Failed to save changes");
            return false;
          }

          setcurrentFormData((prev) => {
            const next = { ...prev, ...fields };
            if (response?.data?.slug != null && "slug" in fields) {
              next.slug = response.data.slug;
            }
            return next;
          });
          if ("profile_image" in fields) setProfileImage("");
          if ("cover_image" in fields) setCoverImage("");

          // The refresh below can still observe a stale listing-label order,
          // so hand the just-saved order to the formData effect.
          if (fields.business_tags) {
            pendingTagOrderRef.current = fields.business_tags;
            formDataRef.current = {
              ...(formDataRef.current || {}),
              business_tags: fields.business_tags,
            };
          }
          await handleRefresh?.();

          const label =
            successMessage ||
            `${FIELD_LABELS[Object.keys(fields)[0]] || "Changes"} saved`;
          toast.success(label);
          return true;
        } catch (err) {
          console.error("Save failed:", err);
          toast.error("Failed to save changes");
          return false;
        }
      };

      // Saves must not overlap: each one round-trips through GHL, and two
      // in-flight updates can land out of order and undo each other.
      const queued = saveQueueRef.current.then(run, run);
      saveQueueRef.current = queued.then(
        () => undefined,
        () => undefined
      );
      return queued;
    },
    [AppService.accounts, currentFormData?.id, formData?.id, handleRefresh]
  );

  const persistFieldsRef = useRef(persistFields);
  useEffect(() => {
    persistFieldsRef.current = persistFields;
  }, [persistFields]);

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    let fieldValue = value;
    if (name === "slug") {
      fieldValue =
        value !== undefined && value !== null ? String(value).trim() : "";
    }
    const savedValue = formDataRef.current?.[name] ?? "";
    if (String(fieldValue ?? "") === String(savedValue ?? "")) return;

    const payloadValue =
      name === "slug" && fieldValue === "" ? undefined : fieldValue;
    persistFields(
      { [name]: payloadValue },
      `${FIELD_LABELS[name] || name} saved`
    );
  };

  const handleBusinessTagsChange = async (selectedOption) => {
    // Stamp order indexes from selection order so refresh/GHL cannot reshuffle.
    const nextTags = Array.isArray(selectedOption)
      ? selectedOption.map((tag, index) => ({
          label: tag.label,
          value: tag.value,
          order: index,
        }))
      : [];
    setcurrentFormData((prev) => ({ ...prev, business_tags: nextTags }));
    // Compare against what was last sent, not what came back: a save may still
    // be queued, and skipping this one would leave that stale value persisted.
    const savedTags =
      submittedTagsRef.current ?? formDataRef.current?.business_tags ?? [];
    const sameOrder =
      nextTags.length === savedTags.length &&
      nextTags.every(
        (tag, i) =>
          String(tag.value) === String(savedTags[i]?.value ?? savedTags[i])
      );
    if (sameOrder) return;
    submittedTagsRef.current = nextTags;
    await persistFields(
      { business_tags: nextTags },
      `${FIELD_LABELS.business_tags} saved`
    );
  };

  const handleAspectRatioChange = async (e) => {
    const value = e.target.value;
    setCoverImageAspectRatio(value);
    if (value === (formDataRef.current?.cover_image_aspect_ratio || "5:2")) return;
    await persistFields(
      { cover_image_aspect_ratio: value },
      `${FIELD_LABELS.cover_image_aspect_ratio} saved`
    );
  };

  const persistImageFields = async (fields, successMessage) =>
    persistFields(fields, successMessage);

  const handleRemoveImage = async (type, idField) => {
    const label = type === "profile_image" ? "Profile" : "Cover";
    setImageUploading(true);
    try {
      await persistImageFields({ [type]: "", [idField]: "" }, `${label} image removed`);
    } finally {
      setImageUploading(false);
    }
  };

  const fileInputRef = useRef(null);

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (file) {
      setCurrentImageType(type);
      openCropModal(file);
      // Store reference to the file input
      fileInputRef.current = e.target;
    }
  };

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const openCropModal = (file) => {
    setTempImageFile(file);
    setCurrentImage(URL.createObjectURL(file));
    setCropModalOpen(true);
  };

  const handleCroppedImage = async (blob) => {
    if (!blob) return;

    setImageUploading(true);
    try {
      const file = new File([blob], "cropped-image.jpg", { type: "image/jpeg" });
      const image_response = await uploadImageToGhl(file, agency);

      if (image_response?.data) {
        const imageUrl = image_response.data;
        if (currentImageType === "profile_image") {
          setProfileImage(imageUrl);
          const saved = await persistImageFields(
            { profile_image: imageUrl },
            "Profile image saved"
          );
          if (saved) setTempImageFile(null);
        } else if (currentImageType === "cover_image") {
          setCoverImage(imageUrl);
          const saved = await persistImageFields(
            { cover_image: imageUrl },
            "Cover image saved"
          );
          if (saved) setTempImageFile(null);
        }
      }
    } catch (err) {
      console.error("Image upload failed:", err);
      toast.error("Failed to upload image");
    } finally {
      setImageUploading(false);
      setCropModalOpen(false);
    }
  };

  const quillRef = useRef(null);
  const quillInstance = useRef(null);

  useEffect(() => {
    if (quillRef.current) {
      if (!quillInstance.current) {
        quillInstance.current = new Quill(quillRef.current, {
          theme: "snow",
        });

        quillInstance.current.on("text-change", () => {
          const html = quillInstance.current.root.innerHTML;
          handleChange({
            target: {
              name: "description",
              value: html,
            },
          });

          if (descriptionSaveTimer.current) {
            clearTimeout(descriptionSaveTimer.current);
          }
          descriptionSaveTimer.current = setTimeout(() => {
            const saved = formDataRef.current?.description ?? "";
            if (html === saved) return;
            persistFieldsRef.current(
              { description: html },
              `${FIELD_LABELS.description} saved`
            );
          }, 800);
        });

        // Save the description only when the editor loses focus (range === null)
        quillInstance.current.on("selection-change", (range, oldRange) => {
          if (range === null && oldRange !== null) {
            const html = quillInstance.current.root.innerHTML;
            const saved = formDataRef.current?.description ?? "";
            if (html === saved) return;
            persistFieldsRef.current(
              { description: html },
              `${FIELD_LABELS.description} saved`
            );
          }
        });
      }

      if (currentFormData?.description && quillInstance.current) {
        if (quillInstance.current.root.innerHTML !== currentFormData.description) {
          quillInstance.current.root.innerHTML = currentFormData.description;
        }
      }
    }
  }, [currentFormData?.description]);

  const ghlBtnProfileRef = useRef(null);
  const ghlBtnProfileTextRef = useRef(null);
  const ghlBtnCoverRef = useRef(null);
  const ghlBtnCoverTextRef = useRef(null);

  const expandGhlButton = (btn, text) => {
    if (btn?.current && text?.current) {
      btn.current.style.width = "180px";
      btn.current.style.paddingRight = "20px";
      text.current.style.opacity = "1";
    }
  };

  const collapseGhlButton = (btn, text) => {
    if (btn?.current && text?.current) {
      btn.current.style.width = "50px";
      btn.current.style.paddingRight = "10px";
      text.current.style.opacity = "0";
    }
  };

  const [ghlGalleryOpen, setGhlGalleryOpen] = useState(false);
  const [ghlImageType, setGhlImageType] = useState("");

  const openGhlGallery = (type) => {
    setGhlImageType(type);
    setGhlGalleryOpen(true);
  };

  const handleGhlImageSelect = async (pick) => {
    if (!pick?.url) return;
    setImageUploading(true);
    try {
      if (ghlImageType === "profile") {
        await persistImageFields({ profile_image: pick?.url, profile_image_id: pick?._id }, "Profile image saved");
      } else if (ghlImageType === "cover") {
        await persistImageFields({ cover_image: pick?.url, cover_image_id: pick?._id }, "Cover image saved");
      }
    } finally {
      setImageUploading(false);
    }
  };

  return (
    <div className="sd-account-detail-tab">
      <>
        <div>
          <>
            <Grid item xs={12} mt={3} display={"flex"} alignItems="center" style={{ padding: 0 }}>
              <label
                className="text-black"
                style={{ marginBottom: "10px", display: "flex", gap: "30px", alignItems: "center" }}
              >
                <span>Profile Image</span>
              </label>
            </Grid>
            <Grid item xs={12} display={"flex"} alignItems="center" style={{ padding: 0 }}>
              <div className="custom-upload-pad" style={{ width: "250px", height: "auto", aspectRatio: "3/2" }}>
                <input type="hidden" name="img_1" value={currentFormData?.profile_image} />

                {currentFormData?.profile_image ? (
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      borderRadius: "8px",
                      overflow: "hidden", // ensures button stays within image
                      display: "block",
                      lineHeight: 0,
                    }}
                  >
                    {/* Uploaded Image */}
                    <img
                      src={
                        profileImage ||
                        currentFormData?.profile_image ||
                        "https://storage.googleapis.com/msgsndr/s8fbgevCLY2YSeR5r9gH/media/af4db2df-cbda-4d6c-83dc-3e593a49aa96.png"
                      }
                      alt="img_1"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        borderRadius: "8px",
                        display: "block",
                        verticalAlign: "top",
                      }}
                    />

                    {/* Remove Button on Top of Image */}
                    <button
                      type="button"
                      disabled={imageUploading}
                      onClick={() => handleRemoveImage("profile_image","profile_image_id")}
                      style={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        backgroundColor: "rgba(0,0,0,0.6)",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "8px",
                      border: "2px dashed #cbd5e1",
                      backgroundColor: "#f8fafc",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#94a3b8",
                      fontSize: "13px",
                      textAlign: "center",
                      padding: "12px",
                    }}
                  >
                    <span style={{ fontWeight: 500, marginBottom: 4 }}>No image selected</span>
                    <span style={{ fontSize: "11px" }}>Use GHL Media or Google to pick one</span>
                  </div>
                )}
              </div>

              {/* GHL Media Gallery Button - to the LEFT of the Google button */}
              <button
                type="button"
                ref={ghlBtnProfileRef}
                style={{
                  marginTop: "100px",
                  marginLeft: "10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  border: "none",
                  background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                  color: "white",
                  padding: "10px",
                  borderRadius: "50px",
                  cursor: "pointer",
                  overflow: "hidden",
                  transition: "width 0.3s ease, padding 0.3s ease, box-shadow 0.3s ease",
                  width: "50px",
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 6px rgba(25, 118, 210, 0.35)",
                }}
                onClick={() => openGhlGallery("profile")}
                onMouseEnter={() => expandGhlButton(ghlBtnProfileRef, ghlBtnProfileTextRef)}
                onMouseLeave={() => collapseGhlButton(ghlBtnProfileRef, ghlBtnProfileTextRef)}
                title="Choose from GHL Media"
              >
                <FaImages size={20} style={{ flexShrink: 0 }} />
                <span
                  ref={ghlBtnProfileTextRef}
                  style={{
                    opacity: 0,
                    color: "white",
                    transition: "opacity 0.3s ease",
                    fontSize: "13px",
                    fontWeight: 500,
                  }}
                >
                  GHL Media
                </span>
              </button>

            </Grid>

            <Grid item xs={12} display={"flex"} alignItems="center" style={{ padding: 0 }}>
              <SoftTypography
                variant="p"
                className="roboto-family"
                style={{ fontSize: "14px", marginTop: "4px" }}
              >
                1MB maximum size. 1:1 ratio. Recommended dimensions: 500 x 500 pixels.
              </SoftTypography>
            </Grid>
          </>
          {/* <div
          style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "20px" }}
        >
          <label
            className="text-black"
            style={{ marginBottom: "10px", display: "flex", gap: "30px", alignItems: "center" }}
          >
            <span>Profile Image</span>
            <img
              src={currentFormData?.profile_image}
              alt="cover_image"
              height={70}
              width={70}
              style={{ borderRadius: "50%" }}
            />
          </label>
          <input type="file" name="profile_image" />
        </div> */}

          <>
            <Grid item xs={12} mt={3} display={"flex"} alignItems="center" style={{ padding: 0 }}>
              <label
                className="text-black"
                style={{ marginBottom: "10px", display: "flex", gap: "30px", alignItems: "center" }}
              >
                <span>Cover Image</span>
              </label>
            </Grid>
            <Grid item xs={12} display={"flex"} alignItems="flex-start" style={{ padding: 0 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <div className="custom-upload-pad" style={{ width: "250px", height: "auto", aspectRatio: coverImageAspectRatio === "4:1" ? "4/1" : "5/2" }}>
                  <input type="hidden" name="img_1" value={currentFormData?.cover_image} />
                  {currentFormData?.cover_image ? (
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        borderRadius: "8px",
                        overflow: "hidden",
                        display: "block",
                        lineHeight: 0,
                      }}
                    >
                      <img
                        src={
                          coverImage ||
                          currentFormData?.cover_image ||
                          "https://storage.googleapis.com/msgsndr/s8fbgevCLY2YSeR5r9gH/media/af4db2df-cbda-4d6c-83dc-3e593a49aa96.png"
                        }
                        alt="img_1"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "center",
                          borderRadius: "8px",
                          display: "block",
                          verticalAlign: "top",
                        }}
                      />
                      <button
                        type="button"
                        disabled={imageUploading}
                        onClick={() => handleRemoveImage("cover_image","cover_image_id")}
                        style={{
                          position: "absolute",
                          top: "5px",
                          right: "5px",
                          backgroundColor: "rgba(0,0,0,0.6)",
                          color: "white",
                          border: "none",
                          borderRadius: "50%",
                          width: "24px",
                          height: "24px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "8px",
                        border: "2px dashed #cbd5e1",
                        backgroundColor: "#f8fafc",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#94a3b8",
                        fontSize: "13px",
                        textAlign: "center",
                        padding: "12px",
                      }}
                    >
                      <span style={{ fontWeight: 500, marginBottom: 4 }}>No image selected</span>
                      <span style={{ fontSize: "11px" }}>Use GHL Media or Google to pick one</span>
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "250px" }}>
                  <div>
                    <label style={{ fontSize: "14px", fontWeight: "500", color: "#333", display: "block", marginBottom: "8px" }}>
                      Aspect Ratio
                    </label>
                    <select
                      value={coverImageAspectRatio}
                      onChange={handleAspectRatioChange}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        fontSize: "14px",
                        border: "1px solid #D2D6DA",
                        borderRadius: "8px",
                        backgroundColor: "white",
                        cursor: "pointer"
                      }}
                    >
                      <option value="5:2">5:2 (Recommended: 1750 x 700 pixels)</option>
                      <option value="4:1">4:1 (Recommended: 2000 x 500 pixels)</option>
                    </select>
                  </div>
                  <SoftTypography
                    variant="p"
                    className="roboto-family"
                    style={{ fontSize: "14px", marginTop: "4px" }}
                  >
                    1MB maximum size. Choose ratio between 5:2 or 4:1.
                  </SoftTypography>
                </div>
              </div>
              {/* GHL Media Gallery Button - to the LEFT of the Google button */}
              <button
                type="button"
                ref={ghlBtnCoverRef}
                style={{
                  marginTop: "100px",
                  marginLeft: "10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  border: "none",
                  background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                  color: "white",
                  padding: "10px",
                  borderRadius: "50px",
                  cursor: "pointer",
                  overflow: "hidden",
                  transition: "width 0.3s ease, padding 0.3s ease, box-shadow 0.3s ease",
                  width: "50px",
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 6px rgba(25, 118, 210, 0.35)",
                }}
                onClick={() => openGhlGallery("cover")}
                onMouseEnter={() => expandGhlButton(ghlBtnCoverRef, ghlBtnCoverTextRef)}
                onMouseLeave={() => collapseGhlButton(ghlBtnCoverRef, ghlBtnCoverTextRef)}
                title="Choose from GHL Media"
              >
                <FaImages size={20} style={{ flexShrink: 0 }} />
                <span
                  ref={ghlBtnCoverTextRef}
                  style={{
                    opacity: 0,
                    color: "white",
                    transition: "opacity 0.3s ease",
                    fontSize: "13px",
                    fontWeight: 500,
                  }}
                >
                  GHL Media
                </span>
              </button>

            </Grid>
          </>

          {/* <div
          style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "20px" }}
        >
          <label
            className="text-black"
            style={{ marginBottom: "10px", display: "", gap: "30px", alignItems: "center" }}
          >
            <span>Cover Image</span>
            <img
              src={currentFormData?.cover_image}
              alt="cover_image"
              height={70}
              width={70}
              style={{ borderRadius: "50%" }}
            />
          </label>
          <input type="file" name="cover_image" />
        </div> */}
          <div style={{ display: "flex", width: "100%" }}>
            <div style={{ width: "50%", padding: "10px 10px 0px 10px" }}>
              <label className="text-black">Listing labels</label>
              <Select
                isMulti
                value={currentFormData?.business_tags}
                onChange={handleBusinessTagsChange}
                options={businessTags}
                placeholder="Select option"
                name="business_tags"
              />
            </div>
            <div style={{ width: "50%", padding: "10px 10px 0px 10px" }}>
              <label className="text-black">Custom slug</label>
              <input
                className="form-control-agust"
                onChange={handleChange}
                onBlur={handleFieldBlur}
                placeholder="e.g. my-business-name"
                name="slug"
                value={currentFormData?.slug || ""}
              />
              <small className="text-muted">URL-friendly identifier for this listing (letters, numbers, hyphens)</small>
            </div>
          </div>
          <div style={{ display: "flex", width: "100%" }}>
            <div style={{ width: "50%", padding: "10px 10px 0px 10px" }}>
              <SoftBox style={{ margin: "10px 0px " }}>
                <label className="text-black">Business Name</label>
                <input
                  className="form-control-agust"
                  label="Business name"
                  onChange={handleChange}
                  onBlur={handleFieldBlur}
                  placeholder="Name"
                  name="first_name"
                  value={currentFormData?.first_name}
                />
              </SoftBox>
              <SoftBox style={{ margin: " 0px " }}>
                <label className="text-black">City</label>
                <input
                  className="form-control-agust"
                  onChange={handleChange}
                  onBlur={handleFieldBlur}
                  placeholder="city"
                  name="city"
                  value={currentFormData?.city}
                />
              </SoftBox>
            </div>
            <div style={{ width: "50%", padding: "10px 10px 0px 10px" }}>
              <SoftBox style={{ margin: "10px 0px " }}>
                <label className="text-black">Website</label>
                <input
                  className="form-control-agust"
                  onChange={handleChange}
                  onBlur={handleFieldBlur}
                  placeholder="website"
                  name="website"
                  value={currentFormData?.website}
                />
              </SoftBox>

              <SoftBox style={{ margin: "10px 0px " }}>
                <label className="text-black">Country</label>
                <input
                  className="form-control-agust"
                  onChange={handleChange}
                  onBlur={handleFieldBlur}
                  placeholder="country"
                  name="country"
                  value={currentFormData?.country}
                />
              </SoftBox>
            </div>
          </div>
          <div style={{ display: "flex", width: "100%" }}>
            <div style={{ width: "50%", padding: "10px 10px 0px 10px" }}>
              <SoftBox style={{ margin: "10px 0px " }}>
                <label className="text-black">State / Prov / Region</label>
                <input
                  className="form-control-agust"
                  onChange={handleChange}
                  onBlur={handleFieldBlur}
                  placeholder="State / Prov / Region"
                  name="state"
                  value={currentFormData?.state}
                />
              </SoftBox>
              <SoftBox style={{ margin: "10px 0px " }}>
                <label className="text-black">Zip / Postal code</label>
                <input
                  className="form-control-agust"
                  onChange={handleChange}
                  onBlur={handleFieldBlur}
                  placeholder="Zip / Postal code"
                  name="zip_code"
                  value={currentFormData?.zip_code}
                />
              </SoftBox>
            </div>
            <div style={{ width: "50%", padding: "10px 10px 0px 10px" }}>
              <SoftBox style={{ margin: "10px 0px " }}>
                <label className="text-black">Street address</label>
                <input
                  className="form-control-agust"
                  onChange={handleChange}
                  onBlur={handleFieldBlur}
                  placeholder="address"
                  name="address"
                  value={currentFormData?.address}
                />
              </SoftBox>

              <SoftBox style={{ margin: "10px 0px " }}>
                <label className="text-black">Time Zone</label>
                <input
                  className="form-control-agust"
                  onChange={handleChange}
                  onBlur={handleFieldBlur}
                  placeholder="time zone"
                  name="time_zone"
                  value={currentFormData?.time_zone}
                />
              </SoftBox>
            </div>
          </div>
          <div style={{ display: "flex", width: "100%" }}>
            <div style={{ width: "50%", padding: "10px 10px 0px 10px" }}>
              <SoftBox style={{ margin: "10px 0px " }}>
                <label className="text-black">Phone </label>
                <input
                  className="form-control-agust"
                  onChange={handleChange}
                  onBlur={handleFieldBlur}
                  placeholder="Phone number"
                  name="phone"
                  value={currentFormData?.phone}
                />
              </SoftBox>
            </div>
            <div style={{ width: "50%", padding: "10px 10px 0px 10px" }}>
              <SoftBox style={{ margin: "10px 0px " }}>
                <GoogleReviews formData={formData} handleRefresh={handleRefresh} />
              </SoftBox>
            </div>
          </div>

          {/* business detail textarea */}
          <div style={{ width: "100%", padding: "10px 10px 0px 10px" }}>
            <SoftBox style={{ margin: "10px 0px " }}>
              <label className="text-black">Business Description</label>
              <div ref={quillRef} className="sd-account-detail-tab__quill" style={{ height: "250px" }} />
            </SoftBox>
          </div>

        </div>

      <ReactCropModal
        open={cropModalOpen}
        onClose={() => {
          clearFileInput();
          setCropModalOpen(false);
        }}
        currentImage={currentImage}
        onCropComplete={handleCroppedImage}
        onClearFile={clearFileInput}
        aspectRatio={
          currentImageType === "profile_image"
            ? 1 / 1
            : coverImageAspectRatio === "4:1"
            ? 4 / 1
            : 5 / 2
        } // 1:1 for profile (500x500), 5:2 or 4:1 for cover
      />

      <GhlMediaGallery
        open={ghlGalleryOpen}
        onClose={() => setGhlGalleryOpen(false)}
        onSelect={handleGhlImageSelect}
        title={`Select ${ghlImageType === "cover" ? "Cover" : "Profile"} Image`}
        enableCrop
        cropAspectRatio={
          ghlImageType === "cover"
            ? coverImageAspectRatio === "4:1"
              ? 4 / 1
              : 5 / 2
            : 1
        }
        googlePlaceId={currentFormData?.google_place_id || placeId}
      />
      </>
    </div>
  );
}

export default ContactTab;
